import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { XMarkIcon, NoSymbolIcon, TrashIcon } from '@heroicons/react/24/outline';
import { format, addDays, parseISO } from 'date-fns';
import { availabilityAPI } from '../../services/api';
import { BreakOccurrence, BreakRecurrenceType } from '../../types/availability';
import { formatClinicDate } from '../../utils/timezone';
import LoadingSpinner from '../common/LoadingSpinner';

interface BreakModalProps {
  isOpen: boolean;
  onClose: () => void;
  onChanged: () => void;       // refetch the calendar after create/delete
  initialDate?: Date;          // pre-fill the date field (defaults to today)
}

// 0=Mon .. 6=Sun (matches backend recurrence_days)
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const toBackendWeekday = (d: Date): number => (d.getDay() + 6) % 7; // JS Sun=0 -> backend Sun=6

const fmtTime = (t: string): string => {
  const [h, m] = t.split(':').map(Number);
  const p = h >= 12 ? 'PM' : 'AM';
  const dh = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${dh}:${m.toString().padStart(2, '0')} ${p}`;
};

const recurrenceLabel = (count: number): string =>
  count > 1 ? `Recurring · ${count} occurrences` : 'One-time';

const BreakModal: React.FC<BreakModalProps> = ({ isOpen, onClose, onChanged, initialDate }) => {
  const today = useMemo(() => new Date(), []);
  const anchor = initialDate ?? today;

  const [dateStr, setDateStr] = useState(format(anchor, 'yyyy-MM-dd'));
  const [startTime, setStartTime] = useState('12:00');
  const [endTime, setEndTime] = useState('13:00');
  const [recurrence, setRecurrence] = useState<BreakRecurrenceType>('one_time');
  const [weekdays, setWeekdays] = useState<number[]>([toBackendWeekday(anchor)]);
  const [reason, setReason] = useState('');

  // Re-seed the date (and weekly default) each time the modal opens or the
  // caller's initialDate changes, so a stale pre-fill from a prior open / a
  // since-navigated calendar week can't linger.
  useEffect(() => {
    if (!isOpen) return;
    const seed = initialDate ?? new Date();
    setDateStr(format(seed, 'yyyy-MM-dd'));
    setWeekdays([toBackendWeekday(seed)]);
  }, [isOpen, initialDate]);

  // Changing the date moves the weekly "repeats on" default to match it.
  const handleDateChange = (value: string) => {
    setDateStr(value);
    if (value) setWeekdays([toBackendWeekday(parseISO(value))]);
  };

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [breaks, setBreaks] = useState<BreakOccurrence[]>([]);
  const [listLoading, setListLoading] = useState(false);

  const refreshList = useCallback(async () => {
    setListLoading(true);
    try {
      const start = format(today, 'yyyy-MM-dd');
      const end = format(addDays(today, 180), 'yyyy-MM-dd');
      setBreaks(await availabilityAPI.getMyBreaks(start, end));
    } catch {
      // non-fatal: the create form still works without the list
    } finally {
      setListLoading(false);
    }
  }, [today]);

  useEffect(() => {
    if (isOpen) {
      refreshList();
    }
  }, [isOpen, refreshList]);

  const toggleWeekday = (day: number) => {
    setWeekdays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day].sort((a, b) => a - b)
    );
  };

  const handleCreate = async () => {
    setError(null);
    if (endTime <= startTime) {
      setError('End time must be after start time.');
      return;
    }
    if (recurrence === 'weekly' && weekdays.length === 0) {
      setError('Select at least one day of the week.');
      return;
    }
    setSaving(true);
    try {
      await availabilityAPI.createBreak({
        date: dateStr,
        start_time: startTime,
        end_time: endTime,
        recurrence_type: recurrence,
        recurrence_days: recurrence === 'weekly' ? weekdays : null,
        reason: reason.trim() || null,
      });
      setReason('');
      await refreshList();
      onChanged();
    } catch (err: any) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      setError(
        status === 409
          ? 'That time overlaps an existing booking and cannot be blocked off.'
          : detail || 'Could not create the break. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  // Group occurrences by series so we can offer "delete series" vs "delete one"
  const grouped = useMemo(() => {
    const map = new Map<string, BreakOccurrence[]>();
    for (const occ of breaks) {
      const key = occ.break_group_id ?? `single-${occ.date}-${occ.start_time}`;
      const arr = map.get(key) ?? [];
      arr.push(occ);
      map.set(key, arr);
    }
    return Array.from(map.entries()).map(([key, occs]) => ({
      key,
      groupId: occs[0].break_group_id,
      reason: occs[0].reason,
      occurrences: occs.sort((a, b) => a.date.localeCompare(b.date)),
    }));
  }, [breaks]);

  const handleDeleteSeries = async (groupId: string | null) => {
    if (!groupId) return;
    try {
      await availabilityAPI.deleteBreak(groupId);
      await refreshList();
      onChanged();
    } catch {
      setError('Could not remove the break series.');
    }
  };

  const handleDeleteOccurrence = async (groupId: string | null, occurrenceDate: string) => {
    if (!groupId) return;
    try {
      await availabilityAPI.deleteBreak(groupId, occurrenceDate);
      await refreshList();
      onChanged();
    } catch {
      setError('Could not remove that break.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-clinic-dark/50 overflow-y-auto h-full w-full z-50">
      <div className="relative mx-auto my-12 p-6 border border-clinic-primary-lighter shadow-lg rounded-card bg-white w-full max-w-lg" data-guide="break-form">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-clinic-dark flex items-center">
            <NoSymbolIcon className="h-6 w-6 text-clinic-dark mr-2" />
            Block off time
          </h3>
          <button
            onClick={onClose}
            className="text-clinic-muted hover:text-clinic-dark transition-colors"
            disabled={saving}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <p className="text-sm text-clinic-muted mb-4">
          Breaks make a time non-bookable. They never affect existing bookings.
        </p>

        {error && (
          <div className="mb-4 p-3 bg-clinic-accent/10 border border-clinic-accent/30 rounded-lg text-sm text-clinic-accent">
            {error}
          </div>
        )}

        {/* Create form */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-clinic-dark mb-1">Date</label>
            <input
              type="date"
              value={dateStr}
              min={format(today, 'yyyy-MM-dd')}
              onChange={e => handleDateChange(e.target.value)}
              className="w-full px-3 py-2 border border-clinic-primary-lighter rounded-lg focus:ring-2 focus:ring-clinic-primary focus:border-clinic-primary"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-clinic-dark mb-1">Start</label>
              <input
                type="time"
                step={900}
                value={startTime}
                onChange={e => setStartTime(e.target.value)}
                className="w-full px-3 py-2 border border-clinic-primary-lighter rounded-lg focus:ring-2 focus:ring-clinic-primary focus:border-clinic-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-clinic-dark mb-1">End</label>
              <input
                type="time"
                step={900}
                value={endTime}
                onChange={e => setEndTime(e.target.value)}
                className="w-full px-3 py-2 border border-clinic-primary-lighter rounded-lg focus:ring-2 focus:ring-clinic-primary focus:border-clinic-primary"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-clinic-dark mb-1">Repeat</label>
            <select
              value={recurrence}
              onChange={e => setRecurrence(e.target.value as BreakRecurrenceType)}
              className="w-full px-3 py-2 border border-clinic-primary-lighter rounded-lg focus:ring-2 focus:ring-clinic-primary focus:border-clinic-primary bg-white"
            >
              <option value="one_time">Does not repeat</option>
              <option value="daily">Every day</option>
              <option value="weekly">Weekly on…</option>
            </select>
            {recurrence !== 'one_time' && (
              <p className="text-xs text-clinic-muted mt-1">Recurs for the next 6 months.</p>
            )}
          </div>

          {recurrence === 'weekly' && (
            <div>
              <label className="block text-sm font-medium text-clinic-dark mb-1">On these days</label>
              <div className="flex flex-wrap gap-2">
                {WEEKDAYS.map((label, idx) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => toggleWeekday(idx)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                      weekdays.includes(idx)
                        ? 'bg-clinic-primary text-white border-clinic-primary'
                        : 'bg-white text-clinic-dark border-clinic-primary-lighter hover:bg-clinic-cream'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-clinic-dark mb-1">Reason (optional)</label>
            <input
              type="text"
              value={reason}
              maxLength={500}
              placeholder="e.g. Lunch, admin time, vacation"
              onChange={e => setReason(e.target.value)}
              className="w-full px-3 py-2 border border-clinic-primary-lighter rounded-lg focus:ring-2 focus:ring-clinic-primary focus:border-clinic-primary"
            />
          </div>

          <button
            data-guide="break-submit"
            onClick={handleCreate}
            disabled={saving}
            className="w-full px-4 py-2 text-sm font-medium text-white bg-clinic-primary rounded-lg hover:bg-clinic-primary-dark transition-colors disabled:opacity-50"
          >
            {saving ? 'Adding break…' : 'Add break'}
          </button>
        </div>

        {/* Manage existing breaks */}
        <div className="mt-6 pt-6 border-t border-clinic-primary-lighter">
          <h4 className="text-sm font-semibold text-clinic-dark mb-3">Upcoming breaks</h4>
          {listLoading ? (
            <div className="flex justify-center py-6">
              <LoadingSpinner size="small" />
            </div>
          ) : grouped.length === 0 ? (
            <p className="text-sm text-clinic-muted">No breaks scheduled in the next 6 months.</p>
          ) : (
            <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
              {grouped.map(group => (
                <div
                  key={group.key}
                  className="p-3 bg-clinic-cream/50 border border-clinic-primary-lighter rounded-lg"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-clinic-dark">
                        {group.reason || 'Break'}
                      </p>
                      <p className="text-xs text-clinic-muted">
                        {recurrenceLabel(group.occurrences.length)} ·{' '}
                        {fmtTime(group.occurrences[0].start_time)}–{fmtTime(group.occurrences[0].end_time)}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDeleteSeries(group.groupId)}
                      className="text-xs font-medium text-clinic-accent hover:underline ml-3 whitespace-nowrap"
                    >
                      {group.occurrences.length > 1 ? 'Delete all' : 'Delete'}
                    </button>
                  </div>
                  {group.occurrences.length > 1 && (
                    <ul className="mt-2 space-y-1">
                      {group.occurrences.slice(0, 8).map(occ => (
                        <li
                          key={`${occ.date}-${occ.start_time}`}
                          className="flex items-center justify-between text-xs text-clinic-muted"
                        >
                          <span>{formatClinicDate(occ.date, 'EEE, MMM d')}</span>
                          <button
                            onClick={() => handleDeleteOccurrence(group.groupId, occ.date)}
                            className="text-clinic-muted hover:text-clinic-accent transition-colors"
                            title="Remove this occurrence"
                          >
                            <TrashIcon className="h-3.5 w-3.5" />
                          </button>
                        </li>
                      ))}
                      {group.occurrences.length > 8 && (
                        <li className="text-xs text-clinic-muted italic">
                          +{group.occurrences.length - 8} more…
                        </li>
                      )}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BreakModal;
